import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  accountOverviewColumnNameMapping,
  alertColumnNameMapping,
  batteryLifeColumnMapping,
  billingColumnNameMapping,
  deviceRecallsColumnMapping,
  leadsRecallsColumnMapping,
  signatureStatusColumnMapping
} from '@/pages/physician/analytics/constants';
import { AccountFilterOption, ColumnSort, SectionKey, SectionKeyOptions } from '@/types/analytics';

interface FilterItem {
  field: string;
  operator: string;
  arguments: string[];
}

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface AnalyticsState {
  selectedAccounts: AccountFilterOption[] | null;
  hasSetSelectedAccountsOnce: boolean;
  overviewDate: string | null;
  alertsDateRange: DateRange;
  billingDateRange: DateRange;
  signatureDateRange: DateRange;
  batteryLifeDateRange: DateRange;
  deviceRecallsDateRange: DateRange;
  leadsRecallsDateRange: DateRange;
  overviewColumnSort: ColumnSort | null;
  alertsColumnSort: ColumnSort | null;
  billingColumnSort: ColumnSort | null;
  signatureColumnSort: ColumnSort | null;
  batteryLifeColumnSort: ColumnSort | null;
  deviceRecallsColumnSort: ColumnSort | null;
  leadsRecallsColumnSort: ColumnSort | null;
  batteryLifePeriod: {
    operator: '<' | '<=' | '>' | '>=' | '=';
    months: number;
  } | null;
  availableColumns: {
    [SectionKeyOptions.OVERVIEW]: typeof accountOverviewColumnNameMapping;
    [SectionKeyOptions.ALERTS]: typeof alertColumnNameMapping;
    [SectionKeyOptions.BILLING]: typeof billingColumnNameMapping;
    [SectionKeyOptions.SIGNATURE]: typeof signatureStatusColumnMapping;
    [SectionKeyOptions.BATTERY_LIFE]: typeof batteryLifeColumnMapping;
    [SectionKeyOptions.LEADS_RECALLS]: typeof leadsRecallsColumnMapping;
    [SectionKeyOptions.DEVICE_RECALLS]: typeof deviceRecallsColumnMapping;
  };
  selectedColumns: {
    [key in SectionKey]?: string[];
  };
  columnFilters: {
    [key in SectionKey]?: FilterItem[];
  };
}

const getInitialDateRange = (): DateRange => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  return {
    startDate: yesterday.toISOString(),
    endDate: today.toISOString()
  };
};

const initialState: AnalyticsState = {
  selectedAccounts: null,
  hasSetSelectedAccountsOnce: false,
  overviewDate: new Date()?.toISOString(),
  alertsDateRange: getInitialDateRange(),
  billingDateRange: getInitialDateRange(),
  signatureDateRange: getInitialDateRange(),
  batteryLifeDateRange: getInitialDateRange(),
  deviceRecallsDateRange: getInitialDateRange(),
  leadsRecallsDateRange: getInitialDateRange(),
  overviewColumnSort: null,
  alertsColumnSort: null,
  billingColumnSort: null,
  signatureColumnSort: null,
  batteryLifeColumnSort: null,
  deviceRecallsColumnSort: null,
  leadsRecallsColumnSort: null,
  batteryLifePeriod: null,
  availableColumns: {
    [SectionKeyOptions.OVERVIEW]: accountOverviewColumnNameMapping,
    [SectionKeyOptions.ALERTS]: alertColumnNameMapping,
    [SectionKeyOptions.BILLING]: billingColumnNameMapping,
    [SectionKeyOptions.SIGNATURE]: signatureStatusColumnMapping,
    [SectionKeyOptions.BATTERY_LIFE]: batteryLifeColumnMapping,
    [SectionKeyOptions.DEVICE_RECALLS]: deviceRecallsColumnMapping,
    [SectionKeyOptions.LEADS_RECALLS]: leadsRecallsColumnMapping
  },
  selectedColumns: {
    [SectionKeyOptions.OVERVIEW]: Object.keys(accountOverviewColumnNameMapping),
    [SectionKeyOptions.ALERTS]: Object.keys(alertColumnNameMapping),
    [SectionKeyOptions.BILLING]: Object.keys(billingColumnNameMapping),
    [SectionKeyOptions.SIGNATURE]: Object.keys(signatureStatusColumnMapping),
    [SectionKeyOptions.BATTERY_LIFE]: Object.keys(batteryLifeColumnMapping),
    [SectionKeyOptions.DEVICE_RECALLS]: Object.keys(deviceRecallsColumnMapping),
    [SectionKeyOptions.LEADS_RECALLS]: Object.keys(leadsRecallsColumnMapping)
  },
  // Initialize with empty filters
  columnFilters: {
    [SectionKeyOptions.OVERVIEW]: [],
    [SectionKeyOptions.ALERTS]: [],
    [SectionKeyOptions.BILLING]: [],
    [SectionKeyOptions.SIGNATURE]: []
  }
};

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setSelectedAccounts: (state, action: PayloadAction<AccountFilterOption[]>) => {
      state.selectedAccounts = action.payload;
    },
    setHasSetSelectedAccountsOnce: (state, action: PayloadAction<boolean>) => {
      state.hasSetSelectedAccountsOnce = action.payload;
    },
    setOverviewDate: (state, action: PayloadAction<Date | null>) => {
      state.overviewDate = action.payload?.toISOString() ?? null;
    },
    setAlertsDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.alertsDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setBillingDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.billingDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setSignatureDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.signatureDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setBatteryLifeDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.batteryLifeDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setBatteryLifePeriod: (
      state,
      action: PayloadAction<{ operator: '<' | '<=' | '>' | '>=' | '='; months: number } | null>
    ) => {
      state.batteryLifePeriod = action.payload;
    },
    setDeviceRecallsDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.deviceRecallsDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setLeadsRecallsDateRange: (
      state,
      action: PayloadAction<{ startDate: Date | null; endDate: Date | null }>
    ) => {
      state.leadsRecallsDateRange = {
        startDate: action.payload.startDate?.toISOString() || null,
        endDate: action.payload.endDate?.toISOString() || null
      };
    },
    setOverviewColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.overviewColumnSort = action.payload;
    },
    setAlertsColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.alertsColumnSort = action.payload;
    },
    setBillingColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.billingColumnSort = action.payload;
    },
    setSignatureColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.signatureColumnSort = action.payload;
    },
    setBatteryLifeColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.batteryLifeColumnSort = action.payload;
    },
    setDeviceRecallsColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.deviceRecallsColumnSort = action.payload;
    },
    setLeadsRecallsColumnSort: (state, action: PayloadAction<ColumnSort | null>) => {
      state.leadsRecallsColumnSort = action.payload;
    },
    setSelectedColumns: (
      state,
      action: PayloadAction<{ sectionKey: SectionKey; columns: string[] }>
    ) => {
      const { sectionKey, columns } = action.payload;
      state.selectedColumns[sectionKey] = columns;
    },
    setColumnFilters: (
      state,
      action: PayloadAction<{
        sectionKey: SectionKey;
        filters: Array<FilterItem>;
      }>
    ) => {
      const { sectionKey, filters } = action.payload;
      state.columnFilters[sectionKey] = filters;
    }
  }
});

export const {
  setSelectedAccounts,
  setHasSetSelectedAccountsOnce,
  setOverviewDate,
  setAlertsDateRange,
  setBillingDateRange,
  setSignatureDateRange,
  setBatteryLifeDateRange,
  setDeviceRecallsDateRange,
  setLeadsRecallsDateRange,
  setOverviewColumnSort,
  setAlertsColumnSort,
  setBillingColumnSort,
  setSignatureColumnSort,
  setBatteryLifeColumnSort,
  setDeviceRecallsColumnSort,
  setLeadsRecallsColumnSort,
  setSelectedColumns,
  setBatteryLifePeriod,
  setColumnFilters
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
